import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { authApi } from "../apis/authApi";
import { usePsaStatus } from "../hooks/usePsaStatus";
import { HaloPsaGuideInner } from "./integrationGuides/HaloPsaGuideInner";
import HaloPermissionFeedbackAlert from "./HaloPermissionFeedbackAlert";
import {
  HaloPermissionFeedback,
  fetchHaloPermissionFeedback,
} from "../utils/haloPermissionChecks";

interface HaloSetupFormProps {
  onBack: () => void;
  onSuccess?: () => void;
  onCredentialsSaved?: (platformName: string) => void;
}

const HaloSetupForm: React.FC<HaloSetupFormProps> = ({ onBack, onSuccess, onCredentialsSaved }) => {
  const { refetch: refetchPsaStatus } = usePsaStatus();
  const [url, setUrl] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [permissionResult, setPermissionResult] = useState<HaloPermissionFeedback | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  const testedValuesRef = useRef<{
    url: string;
    clientId: string;
    clientSecret: string;
  } | null>(null);

  const hasFieldsChanged = () => {
    if (!testedValuesRef.current || !testResult?.success) {
      return true;
    }
    return (
      url !== testedValuesRef.current.url ||
      clientId !== testedValuesRef.current.clientId ||
      clientSecret !== testedValuesRef.current.clientSecret
    );
  };

  const isSaveDisabled = !testResult?.success || hasFieldsChanged() || isSaving;

  /**
   * Strict Halo URL validation for the API: must be a full `http(s)://...` origin.
   */
  const getValidatedHaloUrl = (raw: string): string => {
    const trimmed = String(raw || "").trim().replace(/\/+$/, "");
    if (!trimmed) return "";
    if (!/^https?:\/\//i.test(trimmed)) return "";
    try {
      // Ensures it's parseable as a URL.
      new URL(trimmed);
      return trimmed;
    } catch {
      return "";
    }
  };

  const handleTestConnection = async () => {
    if (!url.trim() || !clientId || !clientSecret) {
      setTestResult({
        success: false,
        message: "Please fill in all required fields before testing the connection.",
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);
    setPermissionResult(null);

    try {
      const validatedUrl = getValidatedHaloUrl(url);
      if (!validatedUrl) {
        setTestResult({
          success: false,
          message: "Halo URL must include http(s)://",
        });
        testedValuesRef.current = null;
        return;
      }
      const result = await authApi.testHaloConnection({
        url: validatedUrl,
        client_id: clientId,
        client_secret: clientSecret,
      });
      setTestResult({
        success: Boolean(result.success),
        message: result.message || "Connection test successful!",
      });

      const permissionFeedback = await fetchHaloPermissionFeedback({
          url: validatedUrl,
          client_id: clientId,
          client_secret: clientSecret,
        });
      setPermissionResult(permissionFeedback);

      const overallSuccess = Boolean(result.success) && permissionFeedback.success;
      if (overallSuccess) {
        testedValuesRef.current = {
          url: validatedUrl,
          clientId,
          clientSecret,
        };
      } else {
        testedValuesRef.current = null;
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || error.message || "Connection test failed. Please check your credentials.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
      setPermissionResult(null);
      testedValuesRef.current = null;
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (isSaveDisabled) {
      return;
    }

    setIsSaving(true);

    try {
      const validatedUrl = getValidatedHaloUrl(url);
      if (!validatedUrl) {
        setTestResult({
          success: false,
          message: "Halo URL must include http(s)://",
        });
        testedValuesRef.current = null;
        return;
      }
      await authApi.saveHaloConfiguration({
        url: validatedUrl,
        client_id: clientId,
        client_secret: clientSecret,
      });

      setShowSuccessSnackbar(true);

      await refetchPsaStatus();

      if (onCredentialsSaved) {
        setTimeout(() => onCredentialsSaved("halo"), 800);
        return;
      }
      if (onSuccess) {
        setTimeout(() => onSuccess(), 1500);
        return;
      }
      setTimeout(() => {
        window.location.href = "/msp-analytics";
      }, 1500);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || "Failed to save Halo configuration.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{
          fontFamily: "var(--theo-font-sans)",
          color: "#6c6c6c",
          textTransform: "none",
          mb: 3,
          "&:hover": {
            background: "#f5f5f5",
          },
        }}
      >
        Back to PSA Selection
      </Button>

      <Typography
        variant="h5"
        component="h2"
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 700,
          mb: 3,
          color: "#1a1a1a",
        }}
      >
        Halo Setup
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 2,
          background: "#f8fafc",
          borderRadius: 2,
          border: "1px solid #e5e5e5",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            mb: 2,
            color: "#1a1a1a",
          }}
        >
          How to connect Halo
        </Typography>
        <Box sx={{ color: "#6c6c6c", "& .MuiTypography-root": { color: "inherit" } }}>
          <HaloPsaGuideInner showGuideTitle={false} />
        </Box>
      </Paper>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          label="URL"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          placeholder="https://your-instance.halopsa.com"
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": { fontFamily: "var(--theo-font-sans)" },
            "& .MuiInputBase-input": { fontFamily: "var(--theo-font-sans)" },
          }}
          helperText="Full instance URL."
        />

        <TextField
          label="Client ID"
          value={clientId}
          onChange={(e) => {
            setClientId(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": { fontFamily: "var(--theo-font-sans)" },
            "& .MuiInputBase-input": { fontFamily: "var(--theo-font-sans)" },
          }}
        />

        <TextField
          label="Client Secret"
          type="password"
          value={clientSecret}
          onChange={(e) => {
            setClientSecret(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": { fontFamily: "var(--theo-font-sans)" },
            "& .MuiInputBase-input": { fontFamily: "var(--theo-font-sans)" },
          }}
        />

        {testResult && (
          <Alert
            severity={testResult.success ? "success" : "error"}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              "& .MuiAlert-message": { fontFamily: "var(--theo-font-sans)" },
            }}
          >
            {testResult.message}
          </Alert>
        )}
        {permissionResult ? (
          <HaloPermissionFeedbackAlert
            feedback={permissionResult}
            onClose={() => setPermissionResult(null)}
          />
        ) : null}
        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleTestConnection}
            disabled={isTesting || !url.trim() || !clientId || !clientSecret}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              borderColor: "#705d4a",
              color: "#705d4a",
              "&:hover": {
                borderColor: "#604d3a",
                background: "#f5f5f5",
              },
              minWidth: 150,
            }}
          >
            {isTesting ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Testing...
              </>
            ) : (
              "Test Connection"
            )}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaveDisabled}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              fontWeight: 500,
              background: isSaveDisabled ? "#e0e0e0" : "#705d4a",
              color: isSaveDisabled ? "#9e9e9e" : "#fff",
              minWidth: 150,
              "&:hover": {
                background: isSaveDisabled ? "#e0e0e0" : "#604d3a",
              },
            }}
          >
            {isSaving ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          sx={{ fontFamily: "var(--theo-font-sans)" }}
        >
          PSA has been successfully connected! Redirecting to dashboard...
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HaloSetupForm;
